import { getSubscriptionCensus } from 'shared/lib/@tanstack-query';

import {
  DiagnosticsChannel,
  isDiagnosticsEnabled,
  logDiagnostic,
} from './firestore-diagnostics';

// 5 minutes × the 2016-entry persistence cap = exactly 7 days of retained
// series. This is the longitudinal growth signal; per-snapshot detail already
// lands in the in-memory ring at full resolution.
const heartbeatIntervalMs = 300_000;

type ChromiumMemory = {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
};

// Chromium-only (so: always present in Electron, usually present in Chrome).
const readHeap = (): ChromiumMemory | undefined =>
  (performance as Performance & { memory?: ChromiumMemory }).memory;

const readStorageEstimate = async () => {
  try {
    const estimate = await window.navigator.storage?.estimate?.();
    return { usageBytes: estimate?.usage, quotaBytes: estimate?.quota };
  } catch {
    return {};
  }
};

let intervalId: ReturnType<typeof setInterval> | undefined;

const sample = async () => {
  // Re-checked per tick: the flag can turn on mid-session when the user resolves.
  if (!isDiagnosticsEnabled()) {
    return;
  }

  const storage = await readStorageEstimate();
  const heap = readHeap();
  const censusSnapshot = getSubscriptionCensus();

  logDiagnostic(DiagnosticsChannel.Heartbeat, () => ({
    liveListeners: censusSnapshot.live,
    totalDocsHeld: censusSnapshot.totalDocs,
    byCollection: censusSnapshot.byCollection,
    usedJSHeapBytes: heap?.usedJSHeapSize,
    totalJSHeapBytes: heap?.totalJSHeapSize,
    storageUsageBytes: storage.usageBytes,
    storageQuotaBytes: storage.quotaBytes,
    isElectron: !!window.electron,
  }));
};

export const startFirestoreDiagnosticsHeartbeat = () => {
  if (intervalId !== undefined) {
    return;
  }
  intervalId = setInterval(() => {
    void sample();
  }, heartbeatIntervalMs);
  void sample();
};

export const stopFirestoreDiagnosticsHeartbeat = () => {
  if (intervalId === undefined) {
    return;
  }
  clearInterval(intervalId);
  intervalId = undefined;
};
